import { useContext, useEffect, useState } from 'react';
import {Calendar, momentLocalizer} from 'react-big-calendar';
import moment from "moment";
import api from "../backends/YiiApi";
import ClickableLink from "../components/utilities/ClickableLink";
import FontAwesome from "../components/utilities/FontAwesome";
import {StoreContext} from "../stores/StoreLoader";
import classNames from 'classnames';
import {observer} from "mobx-react";
import {useMachine} from "@xstate/react/lib";
import {FetchMachine} from "../components/machines/FetchMachine";
import styles from './styles/CalendarPage.module.scss';
import {useTranslation} from "react-i18next";
import {getLocalTimezone} from "../utils/CleversiteUtilities";
import Dropdown from "react-bootstrap/Dropdown";

const localizer = momentLocalizer(moment); // or globalizeLocalizer

export function CalendarShare(props: {
    icalUrl: string,
}) {
    const {icalUrl} = props;

    return <Dropdown>
        <Dropdown.Toggle className="btn btn-default" variant={""}>
            <FontAwesome prefix={'far'} name="fa-share-square"/>&nbsp;Add Calendar
            &nbsp;<span className="caret"/>
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.dropdownMenu} role={"menu"}>
            <ClickableLink
                className={styles.dropdownItem}
                href={`https://www.google.com/calendar/render?cid=${icalUrl}`}
            >
                <FontAwesome prefix={'fab'} fixedWidth
                             className="fa-google-plus-g"/> Add to Google
            </ClickableLink>
            <ClickableLink
                className={styles.dropdownItem}
                href={icalUrl.replace("https\://", "webcal\://")}
            >
                <FontAwesome prefix={'fab'} fixedWidth className="fa-windows"/> Add to
                Outlook
            </ClickableLink>
            <ClickableLink
                className={styles.dropdownItem}
                href={icalUrl.replace("https\://", "webcal\://")}
            >
                <FontAwesome prefix={'fab'} fixedWidth className="fa-apple"/> Add to
                Apple
            </ClickableLink>
            <ClickableLink
                className={styles.dropdownItem}
                href={icalUrl}
            >
                <FontAwesome prefix={'fas'} fixedWidth className="fa-calendar"/> Add to
                other calendar
            </ClickableLink>
        </Dropdown.Menu>
    </Dropdown>
}

function EventView(props) {
    const time = moment(props.event.start).format("h:mma");

    return <div key={`${props.event.title}${props.event.start}`}
                aria-label={`From calendar ${props.event.resources.calendar?.name || ""} ${moment(props.event.start).format("h:mma MMMM Do")} ${props.event.title}`}
    >
        <span style={{
            backgroundColor: props.event.resources.calendar.color,
            width: "10px",
            height: "10px",
            borderRadius: "100%",
            marginRight: "5px",
            display: "inline-block",
        }}/>{props.event.allDay || <span style={{fontWeight: 600}}>{time} </span>}{props.event.title}
    </div>
}

interface EventPropResources extends IEvent {
    calendar: ICalendar
}

interface EventProp {
    title: string,
    start: Date,
    end: Date,
    allDay: boolean,
    resources: EventPropResources
}

interface ICalendar {
    checked: boolean,
    color: string,
    dontInheritCalendar: number,
    events: IEvent[],
    icalUrl: string,
    id: string,
    name: string,
}

const CalendarPage = observer((props) => {
    const {i18nStore, modalStore, organizationStore, interfaceStore} = useContext(StoreContext);
    const [eventSources, setEventSources] = useState<ICalendar[]>(props.calendars);
    const [events, setEvents] = useState<EventProp[]>([]);
    const [current, send] = useMachine(FetchMachine);
    const {t} = useTranslation('translation');

    const [currentRange, setCurrentRange] = useState({
        start: new Date(moment().startOf('month').subtract(3, 'days').format('YYYY-MM-DD')),
        end: new Date(moment().endOf('month').add(3, 'days').format('YYYY-MM-DD')),
    })

    useEffect(() => {
        send("FETCH");
        Promise.all(eventSources.map(source => {
            return new Promise((res, rej) => {
                api?.getEventsBetween(source.id, currentRange.start, currentRange.end, i18nStore.currentOrgSupportedLocale, organizationStore.currentOrganization.id)
                    .done(data => {
                        if (data.events) {
                            res(data.events.map(evt => ({...evt, calendarId: data.id})));
                        }
                    })
                    .fail(() => res([]))
            });
        }))
            .then((results) => {
                send("FULFILL");
                setEvents(results.flat().map((evt: IEvent) => ({
                    title: evt.title,
                    start: new Date(evt.start),
                    end: new Date(evt.end),
                    allDay: evt.allDay,
                    resources: {...evt, calendar: props.calendars.find(c => c.id === evt.calendarId)},
                })));
            });
    }, [currentRange]);

    function handleRangeChange(range) {
        let nextStart, nextEnd;
        if (Array.isArray(range)) {
            nextStart = new Date(moment(range[0]).subtract(3, 'days').format('YYYY-MM-DD'));
            nextEnd = new Date(moment(range[range.length - 1]).add(3, 'days').format('YYYY-MM-DD'));
        } else {
            nextStart = new Date(moment(range.start).subtract(3, 'days').format('YYYY-MM-DD'));
            nextEnd = new Date(moment(range.end).add(3, 'days').format('YYYY-MM-DD'));
        }

        if (nextStart < currentRange.start || nextEnd > currentRange.end) {
            setCurrentRange({
                start: nextStart,
                end: nextEnd,
            })
        }
    }

    const numCalendars = props.calendars.length;

    let content, link;
    if (numCalendars === 1) {
        const thisCal = props.calendars[0];
        const icalUrl = thisCal.icalUrl;

        content = <li key="calendarLabel">
            <span
                style={{fontSize: "1.2em"}}
                data-value={thisCal.id}
                data-color={thisCal.color}
                data-name={thisCal.name}
            >
                <span
                    className="sb-calendar-button-dot"
                    role="img"
                    aria-hidden="true"
                    style={{backgroundColor: thisCal.color}}
                >&nbsp;</span> {thisCal.name} {thisCal.name.toLowerCase().includes("calendar") ? "" : "Calendar"}
            </span>
        </li>;

        link = <CalendarShare icalUrl={icalUrl}/>;
    } else if (numCalendars > 1) {
        content = <>
            <li key="calendarLabel"><span style={{fontSize: "1.2em"}}>Calendars:</span></li>
            {eventSources.map(calendar => {
                const buttonClassName = classNames({
                    ["sb-filter-button"]: true,
                    ["sb-calendar-button"]: true,
                    ["active"]: calendar.checked,
                })
                return <li key={calendar.id}>
                    <button
                        type="button"
                        className={buttonClassName}
                        onClick={() => {
                            setEventSources((sources: ICalendar[]) => {
                                const numSelected = sources.reduce((acc, curr) => acc + (curr.checked ? 1 : 0), 0);
                                const allSelected = numSelected === sources.length;
                                const nowNoneSelected = numSelected === 1 && sources.find(s => s.id === calendar.id)?.checked;

                                return sources.map(source => {
                                    if (nowNoneSelected) {
                                        return {...source, checked: true};
                                    } else if (source.id === calendar.id) {
                                        return {...source, checked: allSelected ? true : !source.checked};
                                    } else if (allSelected) {
                                        return {...source, checked: false};
                                    }
                                    return source;
                                })
                            })
                        }}
                        data-value={calendar.id}
                        data-color={calendar.color}
                        data-name={calendar.name}
                    >
                        <span
                                        className="sb-calendar-button-dot"
                                        role="img"
                                        aria-hidden="true"
                                        style={{backgroundColor: calendar.color}}>&nbsp;</span> {calendar.name}
                    </button>
                </li>
            })}
        </>;

        link = <a href="#" className="sb-display-calendar-list">
            <button type="button" className="btn btn-default" onClick={() => modalStore.addModal({
                type: "calendarList",
            })}>
                <FontAwesome prefix={'far'} name="fa-share-square" style={{marginRight: "0.4em"}}/>{t("Calendar Sync")}
            </button>
        </a>
    }

    function ListEventView(props) {
        function addEventModal(e) {
            if (e.key === "Enter") {
                modalStore.addModal({
                    type: 'schoolBlocksEvent',
                    event: props.event.resources,
                })
            }
        }

        return <button key={`${props.event.title}${props.event.start}`}
                       aria-label={`${props.event.title} at ${moment(props.event.start).format("h:mma MMMM Do")}
                       until ${moment(props.event.end).format("h:mma MMMM Do")}, 
                       from calendar ${props.event.resources.calendar?.name || ""}`}
                       tabIndex={0}
                       onKeyDown={(e) => addEventModal(e)}
        >
            <span style={{
                backgroundColor: props.event.resources.calendar.color,
                width: "10px",
                height: "10px",
                borderRadius: "100%",
                marginRight: "5px",
                display: "inline-block",
            }}/>
            {props.event.title}
        </button>
    }

    const tz = getLocalTimezone();

    return <div id="sb-main-content" className="sb-calendar-container sb-content-width sb-main-content">
        <div className="sb-calendar-block">
            <div className={styles.accessibilityText}
                 tabIndex={0}>
                Please use List view for Accessible Calendar
            </div>
            <div className="calendarList sb-calendar-margin">
                <form>
                    <ul>
                        {content}
                    </ul>
                </form>
            </div>
            <div style={{textAlign: "right"}}>
                {tz &&
                    <span id="sb-calendar-timezone-display" className="pull-left">
                        Timezone: {tz.format('z (Z)')}
                    </span>}
                {link}
            </div>
            <div id="calendarLoading" style={{textAlign: "center"}}>
                <span key="loadingText">{current.value === "PENDING" && t("Loading calendar events...")}</span>
                <div className={styles.calendar}>
                    <Calendar
                        localizer={localizer}
                        events={events.filter(event => {
                            const source = eventSources.find(source => source.id === event.resources.calendarId);
                            return source?.checked;
                        })}
                        onRangeChange={handleRangeChange}
                        components={{
                            month: {
                                event: EventView,
                            },
                            agenda: {
                                event: ListEventView,
                            },
                        }}
                        showAllEvents={true}
                        eventPropGetter={() => ({
                            style: {
                                backgroundColor: "#eee",
                                color: "black",
                                fontSize: "12px",
                            },
                        })}
                        onSelectEvent={event => {
                            modalStore.addModal({
                                type: 'schoolBlocksEvent',
                                event: event.resources,
                            })
                        }}
                        onKeyPressEvent={(event, e) => {
                            if (e.key === "Enter") {
                                modalStore.addModal({
                                    type: 'schoolBlocksEvent',
                                    event: event.resources,
                                })
                            }
                        }}
                        defaultView={interfaceStore.dimensions.width < 900 ? "agenda" : "month"}
                        messages={{
                            agenda: "List",
                        }}
                    />
                </div>
            </div>
            <div id="calendar">
            </div>
        </div>
    </div>
});

export default CalendarPage;
